import { AlertTriangle, Clipboard, ClipboardCheck, Home, RefreshCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
	copied: boolean;
}

/**
 * ErrorBoundary - Catches React errors and displays a user-friendly error page
 *
 * Features:
 * - Displays error message and component stack
 * - "Copy Error" button that copies full error in markdown format
 * - "Reload Page" and "Go Home" actions
 * - Collapsible stack trace for debugging
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
			copied: false,
		};
	}

	static getDerivedStateFromError(error: Error): Partial<State> {
		return {
			hasError: true,
			error,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('ErrorBoundary caught error:', error, errorInfo);
		this.setState({
			error,
			errorInfo,
		});
	}

	handleCopyError = async () => {
		const { error, errorInfo } = this.state;
		if (!error) return;

		// Generate markdown error report
		const markdown = `# Error Report

**Error:** ${error.name || 'Error'}
**Message:** ${error.message}

## Stack Trace

\`\`\`
${error.stack || 'No stack trace available'}
\`\`\`

${
	errorInfo?.componentStack
		? `## Component Stack

\`\`\`
${errorInfo.componentStack}
\`\`\`
`
		: ''
}

---
*Generated at: ${new Date().toISOString()}*
*User Agent: ${navigator.userAgent}*
*URL: ${window.location.href}*
`;

		try {
			await navigator.clipboard.writeText(markdown);
			this.setState({ copied: true });
			setTimeout(() => this.setState({ copied: false }), 2000);
		} catch (err) {
			console.error('Failed to copy error:', err);
		}
	};

	handleReload = () => {
		window.location.reload();
	};

	handleGoHome = () => {
		window.location.href = '/';
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			const { error, errorInfo, copied } = this.state;

			return (
				<div className="min-h-screen bg-background flex items-center justify-center p-4">
					<Card className="max-w-2xl w-full">
						<CardHeader>
							<div className="flex items-start gap-3">
								<AlertTriangle className="h-8 w-8 text-destructive mt-1" />
								<div className="flex-1">
									<CardTitle className="text-2xl">Something went wrong!</CardTitle>
									<CardDescription className="mt-2">
										The application encountered an unexpected error. You can try reloading the page
										or going back to the home page.
									</CardDescription>
								</div>
							</div>
						</CardHeader>

						<CardContent className="space-y-4">
							{/* Error message */}
							{error && (
								<div className="bg-muted p-4 rounded-lg border">
									<div className="font-mono text-sm">
										<div className="font-semibold text-destructive mb-1">
											{error.name || 'Error'}
										</div>
										<div className="text-muted-foreground">{error.message}</div>
									</div>
								</div>
							)}

							{/* Component stack (collapsible) */}
							{errorInfo?.componentStack && (
								<details className="bg-muted p-4 rounded-lg border">
									<summary className="cursor-pointer font-semibold text-sm mb-2 hover:text-primary">
										Component Stack Trace (click to expand)
									</summary>
									<pre className="text-xs overflow-x-auto text-muted-foreground whitespace-pre-wrap">
										{errorInfo.componentStack}
									</pre>
								</details>
							)}

							{/* Action buttons */}
							<div className="flex flex-wrap gap-2 pt-2">
								<Button onClick={this.handleCopyError} variant="outline" size="sm">
									{copied ? (
										<>
											<ClipboardCheck className="h-4 w-4 mr-2" />
											Copied!
										</>
									) : (
										<>
											<Clipboard className="h-4 w-4 mr-2" />
											Copy Error (Markdown)
										</>
									)}
								</Button>
								<Button onClick={this.handleReload} variant="outline" size="sm">
									<RefreshCcw className="h-4 w-4 mr-2" />
									Reload Page
								</Button>
								<Button onClick={this.handleGoHome} variant="default" size="sm">
									<Home className="h-4 w-4 mr-2" />
									Go Home
								</Button>
							</div>

							{/* Developer info */}
							<div className="text-xs text-muted-foreground border-t pt-4">
								<div>
									<strong>Time:</strong> {new Date().toLocaleString()}
								</div>
								<div>
									<strong>URL:</strong> {window.location.href}
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			);
		}

		return this.props.children;
	}
}
